import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import { useAlertStore } from './use-alert-store';
import {
  fetchIikoCredentials,
  fetchIikoMenu,
  fetchIikoMenuList,
  fetchIikoTerminals,
  FetchMenuIikoListRequest,
  FetchMenuIikoTerminalsRequest,
  IikoCreadentialType,
  IikoMenuType,
  postIikoCredentials,
  putIikoCredentials,
  putIikoMenu,
  putIikoMenuOrder,
  syncIikoMenu,
} from './api/iiko-api';
import { IdValue } from '../types/props';

interface Store {
  fetchCredentials: () => Promise<void>;
  putCredentials: () => Promise<void>;
  postCredentials: () => Promise<void>;
  putMenu: (data: Partial<IikoMenuType>) => Promise<void>;
  credentials?: Partial<IikoCreadentialType>;
  isLoading?: boolean;
  pagination?: PaginationType;

  list?: IikoMenuType[];
  menu?: IikoMenuType;
  fetchMenuList: (params: FetchMenuIikoListRequest) => Promise<void>;
  fetchMenu: (id: string) => Promise<void>;
  syncMenu: (id: string) => Promise<void>;
  handleUpdateCredentials: (data: Partial<IikoCreadentialType>) => Promise<void>;
  setIikoMenuListOrder: (order: string[]) => Promise<void>;
  fetchTerminalList: (params: FetchMenuIikoTerminalsRequest) => Promise<void>;
  terminalList?: IdValue[];
}

export const useIikoStore = create<Store>((set, get) => ({
  handleUpdateCredentials: async (data) => {
    set((prev) => ({ ...prev, credentials: { ...prev.credentials, ...data } }));
  },
  fetchCredentials: async () => {
    set({ isLoading: true });
    try {
      const { data } = await fetchIikoCredentials();
      set({ credentials: data });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось получить ключи iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  postCredentials: async () => {
    set({ isLoading: true });
    try {
      const creds = get().credentials;
      if (!creds) {
        throw new Error('Credentials not found');
      }

      const credentials = await postIikoCredentials(creds);
      set({ credentials });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось создать ключи iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  putCredentials: async () => {
    set({ isLoading: true });
    try {
      const creds = get().credentials;
      if (!creds) {
        throw new Error('Credentials not found');
      }

      const credentials = await putIikoCredentials(creds);
      set({ credentials });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось обновить ключи iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  setIikoMenuListOrder: async (order) => {
    set({ isLoading: true });
    try {
      await putIikoMenuOrder(order);

      get().list?.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
      useAlertStore.getState().add({ label: 'Порядок меню успешно сохранен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить порядок меню', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchMenuList: async (params) => {
    set({ isLoading: true });
    try {
      const resp = await fetchIikoMenuList(params);
      set({ list: resp.menus });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось получить меню iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchMenu: async (id) => {
    set({ isLoading: true });
    try {
      const resp = await fetchIikoMenu(id);
      set({ menu: resp.menu });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось получить меню iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  putMenu: async (data) => {
    set({ isLoading: true });
    try {
      const resp = await putIikoMenu(data);
      set({ menu: { ...get().menu, ...resp.menu } });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось обновить меню iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  syncMenu: async (id) => {
    set({ isLoading: true });
    try {
      await syncIikoMenu(id).then(() => get().fetchMenuList({ branch_id: id }));
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось синхронизировать меню iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchTerminalList: async (params) => {
    set({ isLoading: true });
    try {
      const resp = await fetchIikoTerminals(params);
      set({ terminalList: resp });
    } catch (e) {
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось получить терминалы организации iiko',
      });
    } finally {
      set({ isLoading: false });
    }
  },
}));
